/**
 * Validates if a redirect URL is safe and allowed
 * @param url The URL to validate
 * @returns True if the URL is safe to redirect to
 */
function isValidRedirectUrl(url: string): boolean {
  try {
    // Parse the URL to validate its structure
    const parsedUrl = new URL(url, window.location.origin);

    // Only allow same-origin URLs to prevent open redirect attacks
    if (parsedUrl.origin !== window.location.origin) {
      return false;
    }

    // Allow any internal path - React Router will handle unknown routes
    // with appropriate 404 or "page not found" handling
    return true;
  } catch {
    // Invalid URL format
    return false;
  }
}

/**
 * Sanitizes a redirect URL by ensuring it's safe and internal
 * @param url The URL to sanitize
 * @returns A safe redirect URL or null if invalid
 */
function sanitizeRedirectUrl(url: string | null): string | null {
  if (!url) {
    return null;
  }

  // Remove any leading/trailing whitespace
  const trimmedUrl = url.trim();

  if (!trimmedUrl) {
    return null;
  }

  // If it's a relative path, convert to absolute URL for validation
  const absoluteUrl = trimmedUrl.startsWith('/') ? `${window.location.origin}${trimmedUrl}` : trimmedUrl;

  return isValidRedirectUrl(absoluteUrl) ? trimmedUrl : null;
}

/**
 * Extracts the final redirect destination from potentially nested redirectTo parameters
 * This handles cases where users are redirected to /login?redirectTo=/login?redirectTo=/final/path
 */
function extractFinalRedirectDestination(redirectParam: string | null): string | null {
  if (!redirectParam) {
    return null;
  }

  // If the redirect parameter points to another login URL with redirectTo, extract the final destination
  if (redirectParam.startsWith('/login?redirectTo=')) {
    try {
      const loginUrl = new URL(redirectParam, window.location.origin);
      const nestedRedirectTo = loginUrl.searchParams.get('redirectTo');

      // Recursively extract in case of multiple levels of nesting
      return extractFinalRedirectDestination(nestedRedirectTo);
    } catch {
      // If URL parsing fails, return the original parameter
      return redirectParam;
    }
  }

  return redirectParam;
}

/**
 * Extracts redirect URL from URL search parameters with nested redirect handling
 * @param searchParams URLSearchParams object
 * @returns Sanitized redirect URL or null if invalid
 */
export function extractRedirectFromParams(searchParams: URLSearchParams): string | null {
  const rawRedirectTo = searchParams.get('redirectTo');
  const finalRedirectTo = extractFinalRedirectDestination(rawRedirectTo);

  return sanitizeRedirectUrl(finalRedirectTo);
}
